import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    clickSearchedUser,
    deleteAllRecentSearch,
    deleteIndividualRecentSearch,
    recentSearch,
    search,
    searchHashtags,
    searchOnlyPosts,
    searchOnlyUsers,
    searchSuggested,
    searchTrendingPosts,
    searchTrendingUsers,
    groupSearch,
} from "../redux/ApiSlice/SearchSlice";
import { useNavigate, useLocation } from "react-router-dom";
import SearchSection from "../components/search/SearchSection";
import { IoSearch } from "react-icons/io5";
import { IoClose } from "react-icons/io5";
import { groupJoin, cancelGroupJoin } from "../redux/ApiSlice/Group/GroupSlice";
import { ClipLoader } from "react-spinners";
import HashTag from "../components/search/HashTag";
import Posts from "../components/search/Posts";
import Users from "../components/search/Users";
import Groups from "../components/search/Groups";
import Trending from "../components/search/Trending";
import GlobalSearch from "../components/search/GlobalSearch";

export default function SearchResultPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const SearchItems = useSelector((state) => state.SearchItems);
    const ProfileDetails = useSelector((state) => state.AccountProfile);
    const { User_profile } = ProfileDetails;
    const location = useLocation();
    // Access the state passed from navigation
    const { feedType } = location.state || {};
    const capitalizedFeedType = feedType ? feedType.charAt(0).toUpperCase() + feedType.slice(1) : feedType;

    const [SearchItem, setSearchItem] = useState("");
    const [debouncedSearchItem, setDebouncedSearchItem] = useState("");
    const [activeTab, setActiveTab] = useState(() => capitalizedFeedType || "All");
    const [groupSearchData, setGroupSearchData] = useState([]);
    const [recentSearchData, setRecentSearchData] = useState([]);
    const [showRecentSearches, setShowRecentSearches] = useState(true);
    const [showAllRecentSearches, setShowAllRecentSearches] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [postsPage, setPostsPage] = useState(1);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const postsScrollRef = useRef(null);
    const isLoadingRef = useRef(false);
    const RECENT_SEARCH_LIMIT = 5;
    const RECENT_SEARCH_EXPANDED_LIMIT = 15;
    const SEARCH_DEBOUNCE_MS = 1000;
    const MIN_SEARCH_LENGTH = 2;
    const debounceRef = useRef(null);


    const handleSearchBar = (e) => {
        setSearchItem(e.target.value);
        // setShowSuggestions(true);
    };

    const toggleRecentSearches = () => {
        setShowRecentSearches(!showRecentSearches);
    };

    const toggleShowAllRecentSearches = () => {
        setShowAllRecentSearches(!showAllRecentSearches);
    };

    const handleJoinGroup = async (groupId) => {
        try {
            const response = await dispatch(groupJoin(groupId));
            if (response?.payload?.message === "Join request has been sent to the group owner") {
                setGroupSearchData(
                    groupSearchData.map((data) => {
                        if (data.id === groupId) {
                            return { ...data, join_request_status: "pending" };
                        }
                        return data;
                    })
                );
            }
            await dispatch(groupSearch(SearchItem));
        } catch (error) {
            console.error(error);
        }
    };

    const handleCancelJoinRequest = async (groupId) => {
        try {
            await dispatch(cancelGroupJoin(groupId));
            await dispatch(groupSearch(SearchItem));
        } catch (error) {
            console.error(error);
        }
    };

    const handleSearchTrendingUsers = useCallback(async () => {
        try {
            await dispatch(searchTrendingUsers());
        } catch (error) {
            console.error(error);
        }
    }, [dispatch]);

    const handleRecentSearch = useCallback(async () => {
        try {
            await dispatch(recentSearch());
        } catch (error) {
            console.error(error);
        }
    }, [dispatch]);

    const handleClearAllSearch = async () => {
        try {
            await dispatch(deleteAllRecentSearch());
            // If Hashtag tab is active, trigger hashtag search to refresh trending hashtags
            if (activeTab === "Hashtags") {
                let searchTerm = SearchItem;
                if (searchTerm.startsWith("#")) {
                    searchTerm = searchTerm.slice(1);
                }
                await dispatch(search(searchTerm));
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleDeleteIndividualSearch = async (searchId) => {
        try {
            await dispatch(deleteIndividualRecentSearch({ searchId }));
            handleRecentSearch();
        } catch (error) {
            console.error(error);
        }
    };

    const handleNavigate = (id) => {
        if (activeTab === "Users") {
            clickedSearchedUser(id);
        }
        if (User_profile?.id !== id) {
            navigate(`/account/${id}`, { state: { feedType: activeTab } });
        } else {
            navigate("/account", { state: { feedType: activeTab } });
        }
    };

    const trendingUsers = SearchItems.trendingUsers?.results || [];

    useEffect(() => {
        if (SearchItems?.recentSearch?.length > 0) {
            setRecentSearchData(SearchItems?.recentSearch);
        }
        const nextGroupSearch = Array.isArray(SearchItems?.SearchResult?.groups?.results)
            ? SearchItems.SearchResult.groups.results
            : (Array.isArray(SearchItems?.groupSearchResult)
                ? SearchItems.groupSearchResult
                : SearchItems?.groupSearchResult?.results || []);
        setGroupSearchData(nextGroupSearch);
    }, [SearchItems]);

    // Set activeTab from sessionStorage
    useEffect(() => {
        handleRecentSearch();
        const savedTab = capitalizedFeedType || "All";
        if (savedTab) {
            setActiveTab(savedTab);
        }
    }, []);

    // Custom setActiveTab to reset SearchItem when switching to Trending
    const handleSetActiveTab = (tab) => {
        setActiveTab(tab);
        setPostsPage(1); // Reset page when changing tabs
        // Trigger search when tab changes
        if (SearchItem.trim()) {
            const normalizedQuery = SearchItem.trim().startsWith("#")
                ? SearchItem.trim().slice(1)
                : SearchItem.trim();

            if (tab === "Posts") {
                dispatch(searchOnlyPosts({ query: normalizedQuery, page: 1 }));
            } else if (tab === "Users") {

                dispatch(searchOnlyUsers({ query: normalizedQuery, page: 1 }));
            } else if (tab === "Trending") {
                dispatch(searchTrendingPosts({ page: 1 }));
            } else if (tab === "Groups" || tab === "All") {
                dispatch(search(normalizedQuery));
            }
        } else if (tab === "Trending") {
            dispatch(searchTrendingPosts({ page: 1 }));
        } else if (tab === "Users") {
            dispatch(searchTrendingUsers({ page: 1 }));
        } else if (tab === "Hashtags") {
            dispatch(searchHashtags({ query: "", page: 1 }));
        }
        else if (tab === "Posts") {
            dispatch(searchOnlyPosts({ query: "", page: 1 }));
        }
    };


    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        const nextValue = SearchItem.trim();
        debounceRef.current = setTimeout(() => {
            setDebouncedSearchItem(nextValue);
        }, SEARCH_DEBOUNCE_MS);
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [SearchItem]);

    const runSearch = useCallback((query) => {
        const normalizedQuery = query.startsWith("#")
            ? query.slice(1)
            : query;
        if (activeTab === "Trending") {
            handleSearchTrendingUsers();
            // dispatch(search(normalizedQuery));
            return;
        }
        if (query === "") {
            // if (activeTab === "All" || activeTab === "Users" || activeTab === "Posts" || activeTab === "Groups" || activeTab === "Hashtags") {
            //     dispatch(search(""));
            // } else {
            handleRecentSearch();
            // }
            return;
        }

        if (query.length < MIN_SEARCH_LENGTH && activeTab !== "Hashtags") {
            return;
        }

        if (activeTab === "Groups") {
            dispatch(search(normalizedQuery));
        } else if (activeTab === "Hashtags") {
            // dispatch(search(normalizedQuery));
            dispatch(searchHashtags({ query: normalizedQuery, page: 1 }));
        } else if (activeTab === "Posts") {
            setPostsPage(1); // Reset page for new search
            dispatch(searchOnlyPosts({ query: normalizedQuery, page: 1 }));
        } else if (activeTab === "Users") {
            dispatch(searchTrendingUsers(
                { page: 1 }
            ));
            dispatch(searchOnlyUsers({ query: normalizedQuery, page: 1 }));
        } else if (activeTab === "All") {
            dispatch(search(normalizedQuery));
        }
    }, [activeTab, dispatch, handleRecentSearch, handleSearchTrendingUsers]);

    const runSuggestedSearch = useCallback((query) => {
        const normalizedQuery = query.startsWith("#")
            ? query.slice(1)
            : query;

        if (query === "" || query.length < MIN_SEARCH_LENGTH) {
            setShowSuggestions(false);
            return;
        }
        dispatch(searchSuggested(normalizedQuery));
        setShowSuggestions(true);
    }, [dispatch]);

    const clickedSearchedUser = useCallback((query) => {
        // dispatch(clickSearchedUser(query));
    }, [dispatch]);

    const handleSuggestionClick = (suggestion) => {
        // setDebouncedSearchItem(suggestion);
        // if (debounceRef.current) {
        //     clearTimeout(debounceRef.current);
        // }
        runSearch(suggestion);
        setShowSuggestions(false);
    };

    useEffect(() => {
        if (debouncedSearchItem && debouncedSearchItem.length >= MIN_SEARCH_LENGTH) {
            runSuggestedSearch(debouncedSearchItem);
        } else {
            setShowSuggestions(false);
        }
    }, [debouncedSearchItem, runSuggestedSearch]);

    const handleSearchNow = () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        const nextValue = SearchItem.trim();
        setDebouncedSearchItem(nextValue);
        setShowSuggestions(false);
        setPostsPage(1); // Reset page on new search
        runSearch(nextValue);
    };

    // Reset loading ref when loading completes
    useEffect(() => {
        if (!isLoadingMore) {
            isLoadingRef.current = false;
        }
    }, [isLoadingMore]);

    const handlePostsScroll = useCallback(() => {
        const container = postsScrollRef.current;

        if (!container || isLoadingRef.current) {
            return;
        }

        // Determine hasNext based on active tab
        let hasNext = false;
        if (activeTab === "Trending") {
            hasNext = SearchItems?.trendingPostSearchResult?.next;
        } else if (activeTab === "Posts") {
            hasNext = SearchItems?.postsSearchResult?.posts?.next;
        } else if (activeTab === "Users") {
            if (SearchItem.trim()) {
                hasNext = SearchItems?.SearchResult?.next;
            } else {
                hasNext = SearchItems?.trendingUsers?.next;
            }
        } else if (activeTab === "Hashtags") {
            hasNext = SearchItems?.hashtagSearchResult?.next;
        }


        if (!hasNext) {
            return;
        }

        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;
        const scrollPosition = scrollTop + clientHeight;
        const hasReachedBottom = scrollPosition >= scrollHeight - 100; // 100px threshold

        if (hasReachedBottom) {
            // Prevent duplicate calls
            isLoadingRef.current = true;
            setIsLoadingMore(true);
            const nextPage = postsPage + 1;
            setPostsPage(nextPage);

            const normalizedQuery = SearchItem.startsWith("#")
                ? SearchItem.slice(1)
                : SearchItem;

            if (activeTab === "Trending") {
                dispatch(searchTrendingPosts({ page: nextPage })).finally(() => {
                    setIsLoadingMore(false);
                    setTimeout(() => {
                        isLoadingRef.current = false;
                    }, 1000);
                });
                return;
            }

            if (activeTab === "Hashtags") {
                dispatch(searchHashtags({ query: normalizedQuery, page: nextPage })).finally(() => {
                    setIsLoadingMore(false);
                    setTimeout(() => {
                        isLoadingRef.current = false;
                    }, 1000);
                });
                return;
            }

            if (activeTab === "Users" && !SearchItem) {
                dispatch(searchTrendingUsers({ page: nextPage })).finally(() => {
                    setIsLoadingMore(false);
                    setTimeout(() => {
                        isLoadingRef.current = false;
                    }, 1000);
                });
                return;
            }

            if (activeTab === "Posts") {
                dispatch(searchOnlyPosts({ query: normalizedQuery, page: nextPage }))
                    .finally(() => {
                        setIsLoadingMore(false);
                        setTimeout(() => {
                            isLoadingRef.current = false;
                        }, 1000);
                    });
            } else if (activeTab === "Users") {
                dispatch(searchOnlyUsers({ query: normalizedQuery, page: nextPage }))
                    .finally(() => {
                        setIsLoadingMore(false);
                        setTimeout(() => {
                            isLoadingRef.current = false;
                        }, 1000);
                    });
            }
        }
    }, [activeTab, isLoadingMore, SearchItems, postsPage, SearchItem, dispatch]);

    useEffect(() => {
        const container = postsScrollRef.current;
        if (!container) return;

        container.addEventListener("scroll", handlePostsScroll);

        return () => {
            container.removeEventListener("scroll", handlePostsScroll);
        };
    }, [handlePostsScroll]);

    return (
        <div className="relative h-full flex flex-col">
            <div className={`h-full bg-white dark:bg-[#1E1D21] text-black dark:text-white flex flex-col relative`}>
                <SearchSection
                    navigate={navigate}
                    activeTab={activeTab}
                    SearchItem={SearchItem}
                    setActiveTab={handleSetActiveTab}
                    handleSearchBar={handleSearchBar}
                    handleSearchfun={handleSearchNow}
                />
                <div
                    ref={postsScrollRef}
                    className="dark:bg-[#1E1D21] bg-white flex-1 relative overflow-y-auto no-scrollbar overflow-x-hidden"
                >
                    {/* Search Suggestions Dropdown */}
                    {showSuggestions &&
                        SearchItem.length >= MIN_SEARCH_LENGTH &&
                        SearchItems?.SuggestedSearchResult?.suggestions?.length > 0 && (
                            <div className="px-3 py-2 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                                <p className="text-[12px] font-semibold text-gray-500 dark:text-white mb-2">Suggestions</p>
                                <div className="space-y-1">
                                    {SearchItems.SuggestedSearchResult.suggestions.slice(0, 8).map((suggestion, index) => (
                                        <div
                                            key={index}
                                            onClick={() => handleSuggestionClick(suggestion)}
                                            className="flex items-center gap-x-2 px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded cursor-pointer transition-colors"
                                        >
                                            <IoSearch className="text-[16px] text-gray-500 dark:text-gray-600" />
                                            <p className="text-[13px] font-medium text-black dark:text-white">
                                                {suggestion}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                    {SearchItems.loading && !isLoadingMore && (
                        <div className="flex  justify-center items-center h-[25dvh]">
                            <ClipLoader
                                size={30}
                                color={"#CAD2C5"}
                            />
                        </div>
                    )}

                    {SearchItem === "" &&
                        activeTab !== "Groups" &&
                        activeTab !== "Users" &&
                        activeTab !== "Trending" &&
                        activeTab !== "Hashtags" &&
                        activeTab !== "Posts" &&
                        !showSuggestions &&
                        !SearchItems.loading && (
                            <div className="px-3">
                                {SearchItems?.recentSearch?.length !== 0 ? (
                                    <div className="">
                                        <div className="flex justify-between items-center pb-3">
                                            <p
                                                onClick={toggleRecentSearches}
                                                className="text-[14px] font-bold text-black dark:text-white cursor-pointer"
                                            >
                                                Recent Searches
                                            </p>
                                            {/* {showRecentSearches &&
                                                <p
                                                    onClick={() => handleClearAllSearch()}
                                                    className="text-[12px] font-bold text-blue-800 dark:text-blue-400 cursor-pointer"
                                                >
                                                    Clear all
                                                </p>
                                            } */}
                                        </div>
                                        {showRecentSearches && (
                                            <>
                                                {(showAllRecentSearches
                                                    ? recentSearchData?.slice(0, RECENT_SEARCH_EXPANDED_LIMIT)
                                                    : recentSearchData?.slice(0, RECENT_SEARCH_LIMIT)
                                                )?.map((sub, index) => (
                                                    <div key={index} className="w-full flex items-center justify-between gap-x-5 mb-2.5">
                                                        <div className="flex items-center gap-x-1.5">
                                                            <IoSearch className="text-[18px] text-gray-700 dark:text-gray-400" />
                                                            <p
                                                                onClick={() => { setSearchItem(sub.query) }}
                                                                className="text-[13px] font-semibold line-clamp-1 text-black dark:text-white cursor-pointer"
                                                            >
                                                                {sub.query}
                                                            </p>
                                                        </div>
                                                        <IoClose
                                                            onClick={() => handleDeleteIndividualSearch(sub?.id)}
                                                            className="cursor-pointer text-black dark:text-white hover:text-black/80 dark:hover:text-white/80 text-[18px]"
                                                        />
                                                    </div>
                                                ))}
                                                {recentSearchData?.length > RECENT_SEARCH_LIMIT && (
                                                    <p
                                                        onClick={toggleShowAllRecentSearches}
                                                        className="text-[12px] font-semibold text-center text-gray-800 dark:text-gray-400 cursor-pointer hover:text-gray-500 dark:hover:text-gray-300"
                                                    >
                                                        {/* {showAllRecentSearches ? 'Show less' : `See more (${recentSearchData.length - RECENT_SEARCH_LIMIT})`} */}
                                                        {showAllRecentSearches ? 'Show less' : `See more`}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex justify-center items-center h-[25vh]">
                                        <p className="text-[12px] text-gray-500">
                                            No recent searches...
                                        </p>
                                    </div>
                                )}
                            </div>
                        )
                    }

                    {activeTab === "Groups" && !SearchItems.loading && !showSuggestions && (
                        <Groups
                            SearchItem={SearchItem}
                            loading={SearchItems.loading}
                            groupSearchData={groupSearchData}
                            handleJoinGroup={handleJoinGroup}
                            handleCancelJoinRequest={handleCancelJoinRequest}
                        />
                    )}

                    {activeTab === "Hashtags" && !SearchItems.loading && (
                        <HashTag
                            hashtagData={SearchItems?.hashtagSearchResult?.results}
                        />
                    )}

                    {
                        console.log(SearchItems?.hashtagSearchResult, "SearchItems?.hashtagSearchResult")
                    }

                    {(activeTab === "Posts") && !SearchItems.loading && !showSuggestions && (
                        <>
                            <Posts
                                loading={SearchItems.loading}
                                posts={
                                    Array.isArray(SearchItems?.postsSearchResult?.posts?.results)
                                        ? SearchItems.postsSearchResult.posts.results
                                        : (Array.isArray(SearchItems?.postsSearchResult?.posts)
                                            ? SearchItems.postsSearchResult.posts
                                            : [])
                                }
                            />
                            {isLoadingMore && (
                                <div className="flex justify-center items-center py-4">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                                </div>
                            )}
                        </>
                    )}
                    {activeTab === "All" && SearchItem !== "" &&
                        !SearchItems.loading && !showSuggestions && (
                            <>
                                <GlobalSearch
                                    SearchItems={SearchItems}
                                    loading={SearchItems.loading}
                                    handleNavigate={handleNavigate}
                                    groupSearchData={groupSearchData}
                                    handleJoinGroup={handleJoinGroup}
                                    usersData={SearchItems?.SearchResult?.users?.results?.length > 0 ? SearchItems?.SearchResult?.users?.results : trendingUsers}
                                    posts={SearchItems?.SearchResult?.posts || []}
                                />
                                {isLoadingMore && (
                                    <div className="flex justify-center items-center py-4">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                                    </div>
                                )}
                            </>
                        )}

                    {activeTab === "Trending" && SearchItems.loading === false && !showSuggestions && (
                        <>
                            <Trending
                                SearchItem={SearchItem}
                                loading={SearchItems.loading}
                                handleNavigate={handleNavigate}
                                // usersData={trendingUsers || []}
                                posts={SearchItems?.trendingPostSearchResult?.results}
                            />
                            {isLoadingMore && (
                                <div className="flex justify-center items-center py-4">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                                </div>
                            )}
                        </>
                    )}

                    {activeTab === "Users" && !SearchItems.loading && !showSuggestions && (
                        <>
                            <Users
                                handleNavigate={handleNavigate}
                                usersData={
                                    SearchItems?.SearchResult?.results?.length > 0 ? SearchItems?.SearchResult?.results : trendingUsers
                                }
                                trendingUsersTitle={SearchItems?.SearchResult?.results?.length > 0 ? null : "Trending Users"}
                            />
                            {isLoadingMore && (
                                <div className="flex justify-center items-center py-4">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
